// Goals onboarding questionnaire (spec 4) and the plan review (spec 5). Four steps:
// about you → your numbers → your situation (private) → your plan (editable assumptions,
// realism check, urgency tier). Saving creates a new goal version.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useUserId } from '../../lib/auth';
import { money } from '../../lib/format';
import { backSolve, horizonMonths, METRIC_LABEL, ROLE_TEMPLATES, ROLES, urgencyTier, type Role, type UrgencyAnswers, type UrgencyTier } from '../../lib/goals/engine';
import {
  BROKERAGE_TYPES,
  CHANNELS,
  defaultStream,
  EXPERIENCE_LABEL,
  planInput,
  PROPERTY_TYPES,
  saveProfile,
  type GoalAnswers,
  type GoalProfileRow,
} from '../../lib/goals/data';
import { Button, cx, ErrorBox, Field, FieldGroup, Input, Select } from '../ui';

const STEPS = ['About you', 'Your numbers', 'Your situation', 'Your plan'];

function Chips<T extends string>({ options, value, onChange, labels }: { options: readonly T[]; value: T[]; onChange: (v: T[]) => void; labels?: Record<string, string> }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button
            key={o}
            onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
            className={cx('rounded-full border px-3 py-1 text-sm', on ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300')}
          >
            {labels?.[o] ?? o}
          </button>
        );
      })}
    </div>
  );
}

function Choice<T extends string>({ options, value, onChange }: { options: { key: T; label: string }[]; value: T | null; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={cx('rounded-md border px-3 py-1 text-sm', value === o.key ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const YesNo = ({ value, onChange }: { value: boolean | null; onChange: (v: boolean) => void }) => (
  <Choice options={[{ key: 'yes', label: 'Yes' }, { key: 'no', label: 'No' }]} value={value == null ? null : value ? 'yes' : 'no'} onChange={(v) => onChange(v === 'yes')} />
);

const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(/[$,%\s]/g, '')));
const pct = (n: number) => +(n * 100).toFixed(2);

export function SetupWizard({ initial, initialUrgency, current, startStep = 0, onDone }: { initial: GoalAnswers; initialUrgency: UrgencyAnswers; current: GoalProfileRow | null; startStep?: number; onDone: () => void }) {
  const qc = useQueryClient();
  const userId = useUserId();
  const [step, setStep] = useState(startStep);
  const [a, setA] = useState<GoalAnswers>(initial);
  const [u, setU] = useState<UrgencyAnswers>(initialUrgency);
  const [override, setOverride] = useState<UrgencyTier | null>(current?.urgency_override ?? null);
  const [horizonEdited, setHorizonEdited] = useState<number | null>(current?.horizon_months ?? null);
  const set = <K extends keyof GoalAnswers>(k: K, v: GoalAnswers[K]) => setA((x) => ({ ...x, [k]: v }));

  const setRoles = (roles: Role[]) =>
    setA((x) => ({
      ...x,
      roles,
      streams: roles.map((r) => x.streams.find((s) => s.role === r) ?? defaultStream(r, x.experience, 1)),
    }));
  const setStream = (role: Role, patch: Partial<GoalAnswers['streams'][number]>) => setA((x) => ({ ...x, streams: x.streams.map((s) => (s.role === role ? { ...s, ...patch } : s)) }));

  const hasPipeline = (a.pipeline.activeListings ?? 0) + (a.pipeline.activeClients ?? 0) > 0;
  const suggestedHorizon = a.roles.length ? horizonMonths(a.roles, a.experience, hasPipeline, !!u.connections) : 15;
  const horizon = horizonEdited ?? suggestedHorizon;
  const plan = backSolve(planInput(a, u));
  const tier = urgencyTier(u);

  const save = useMutation({
    mutationFn: () => saveProfile({ answers: a, urgency: u, override, horizon, current, userId }),
    onSuccess: () => {
      qc.invalidateQueries();
      onDone();
    },
  });

  const canNext = step === 0 ? a.roles.length > 0 && a.propertyTypes.length > 0 && a.city.trim() !== '' : step === 1 ? a.incomeGoal > 0 && a.streams.every((s) => (s.feeMode === 'pct' ? s.dealSize > 0 && s.feePct > 0 : s.feeAmount > 0)) : true;

  return (
    <div className="space-y-5">
      <ol className="flex flex-wrap gap-2 text-sm">
        {STEPS.map((s, i) => (
          <li key={s}>
            <button
              onClick={() => i < step && setStep(i)}
              className={cx('rounded-full px-3 py-1', i === step ? 'bg-indigo-600 font-medium text-white' : i < step ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100' : 'bg-slate-100 text-slate-500')}
            >
              {i + 1}. {s}
            </button>
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="space-y-4">
          <Field label="First name (the coach uses it)">
            <Input value={a.firstName} onChange={(e) => set('firstName', e.target.value)} className="!w-56" />
          </Field>
          <FieldGroup label="How long have you been in the business?">
            <Choice options={Object.entries(EXPERIENCE_LABEL).map(([key, label]) => ({ key: key as GoalAnswers['experience'], label }))} value={a.experience} onChange={(v) => set('experience', v)} />
          </FieldGroup>
          <FieldGroup label="Your role(s) — each becomes its own revenue stream">
            <Chips options={ROLES} value={a.roles} onChange={setRoles} labels={Object.fromEntries(ROLES.map((r) => [r, ROLE_TEMPLATES[r].label]))} />
          </FieldGroup>
          <FieldGroup label="Property types you focus on">
            <Chips options={PROPERTY_TYPES} value={a.propertyTypes} onChange={(v) => set('propertyTypes', v)} />
          </FieldGroup>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="City and state">
              <Input value={a.city} onChange={(e) => set('city', e.target.value)} placeholder="Atlanta, GA" />
            </Field>
            <Field label="Territory (optional)">
              <Input value={a.territory} onChange={(e) => set('territory', e.target.value)} placeholder="Submarkets, or none" />
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FieldGroup label="Team or solo">
              <Choice options={[{ key: 'solo', label: 'Solo' }, { key: 'team', label: 'Team' }]} value={a.team} onChange={(v) => set('team', v)} />
            </FieldGroup>
            <Field label="Brokerage type (optional)">
              <Select value={a.brokerageType} onChange={(e) => set('brokerageType', e.target.value)}>
                <option value="">—</option>
                {BROKERAGE_TYPES.map((b) => (
                  <option key={b}>{b}</option>
                ))}
              </Select>
            </Field>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <Field label="Realistic income goal for the year" hint="Net to you after splits, before taxes.">
            <Input inputMode="numeric" value={a.incomeGoal ? a.incomeGoal.toLocaleString() : ''} onChange={(e) => set('incomeGoal', num(e.target.value) ?? 0)} className="!w-48" />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Your share after the house (%)">
              <Input inputMode="decimal" value={a.houseSplitPct} onChange={(e) => set('houseSplitPct', num(e.target.value) ?? 0)} className="!w-28" />
            </Field>
            <Field label="Your share after a co-broker / team split (%)" hint="100 if you usually don't split.">
              <Input inputMode="decimal" value={a.coBrokerSplitPct} onChange={(e) => set('coBrokerSplitPct', num(e.target.value) ?? 0)} className="!w-28" />
            </Field>
          </div>
          <p className="text-xs text-slate-500">Version 1 handles a flat split plus one second split; caps, tiers and referral fees aren't modeled yet.</p>
          {a.streams.map((s) => (
            <div key={s.role} className="rounded-md border border-slate-200 p-3">
              <div className="mb-2 font-medium text-slate-800">{ROLE_TEMPLATES[s.role].label}: typical deal</div>
              <div className="flex flex-wrap items-end gap-3">
                <FieldGroup label="Fee as">
                  <Choice options={[{ key: 'pct', label: '% of deal size' }, { key: 'amount', label: '$ per deal' }]} value={s.feeMode} onChange={(v) => setStream(s.role, { feeMode: v })} />
                </FieldGroup>
                {s.feeMode === 'pct' ? (
                  <>
                    <Field label="Deal size ($)">
                      <Input inputMode="numeric" value={s.dealSize ? s.dealSize.toLocaleString() : ''} onChange={(e) => setStream(s.role, { dealSize: num(e.target.value) ?? 0 })} className="!w-40" />
                    </Field>
                    <Field label="Fee (%)">
                      <Input inputMode="decimal" value={pct(s.feePct)} onChange={(e) => setStream(s.role, { feePct: (num(e.target.value) ?? 0) / 100 })} className="!w-24" />
                    </Field>
                  </>
                ) : (
                  <Field label="Fee per deal ($)">
                    <Input inputMode="numeric" value={s.feeAmount ? s.feeAmount.toLocaleString() : ''} onChange={(e) => setStream(s.role, { feeAmount: num(e.target.value) ?? 0 })} className="!w-40" />
                  </Field>
                )}
                {a.streams.length > 1 && (
                  <Field label="Share of income goal (%)">
                    <Input inputMode="decimal" value={pct(s.share / a.streams.reduce((t, x) => t + x.share, 0))} onChange={(e) => setStream(s.role, { share: (num(e.target.value) ?? 0) / 100 })} className="!w-24" />
                  </Field>
                )}
              </div>
            </div>
          ))}
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Weeks you'll work this year">
              <Input inputMode="numeric" value={a.workingWeeks} onChange={(e) => set('workingWeeks', num(e.target.value) ?? 48)} className="!w-24" />
            </Field>
            <Field label="Hours a week for prospecting (optional)">
              <Input inputMode="numeric" value={a.prospectingHours ?? ''} onChange={(e) => set('prospectingHours', num(e.target.value))} className="!w-24" />
            </Field>
            <Field label="Calls per hour (assumption)">
              <Input inputMode="numeric" value={a.callsPerHour} onChange={(e) => set('callsPerHour', num(e.target.value) ?? 15)} className="!w-24" />
            </Field>
          </div>
          <FieldGroup label="Current pipeline and book (optional)">
            <div className="flex flex-wrap gap-3">
              {(
                [
                  ['activeListings', 'Active listings'],
                  ['activeClients', 'Active clients'],
                  ['databaseSize', 'Database size'],
                ] as const
              ).map(([k, label]) => (
                <label key={k} className="text-sm text-slate-600">
                  {label}
                  <Input inputMode="numeric" value={a.pipeline[k] ?? ''} onChange={(e) => set('pipeline', { ...a.pipeline, [k]: num(e.target.value) })} className="mt-1 !w-28" />
                </label>
              ))}
            </div>
          </FieldGroup>
          <FieldGroup label="Prospecting channels you'll use (optional)">
            <Chips options={CHANNELS} value={a.channels} onChange={(v) => set('channels', v)} />
          </FieldGroup>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="flex items-start gap-2 rounded-md bg-slate-50 p-3 text-sm text-slate-600">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            Private: only you can see these answers — never teammates, managers or admins. They set how hard the coach pushes and how long the ramp is; they never change your targets.
          </p>
          <FieldGroup label="Financial support (savings, a partner's income, family help)?">
            <Choice options={[{ key: 'none', label: 'None' }, { key: 'some', label: 'Some' }, { key: 'substantial', label: 'Substantial' }]} value={u.support} onChange={(v) => setU({ ...u, support: v })} />
          </FieldGroup>
          <FieldGroup label="Savings (optional)">
            <Choice
              options={[
                { key: 'none', label: 'None' },
                { key: 'lt6', label: 'Under 6 months of expenses' },
                { key: '6to12', label: '6–12 months' },
                { key: '12plus', label: '12+ months' },
              ]}
              value={u.savings}
              onChange={(v) => setU({ ...u, savings: v })}
            />
          </FieldGroup>
          {(
            [
              ['passiveIncome', 'Passive income?'],
              ['secondJob', 'A second job?'],
              ['familyCovers', 'Living with family, or family covering living costs?'],
              ['connections', 'Family or personal connections in commercial real estate?'],
            ] as const
          ).map(([k, label]) => (
            <FieldGroup key={k} label={label}>
              <YesNo value={u[k]} onChange={(v) => setU({ ...u, [k]: v })} />
            </FieldGroup>
          ))}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            The math, worked backwards from <b>{money(a.incomeGoal, false)}</b> net. Rates are <b>starting assumptions — edit them</b>; once you have 90 days or 100 logged calls, the app will
            offer your real rates.
          </p>
          {plan.streams.map((s) => {
            const input = a.streams.find((x) => x.role === s.role)!;
            return (
              <div key={s.role} className="rounded-md border border-slate-200 p-3">
                <div className="mb-1 font-medium text-slate-800">
                  {s.label} · {money(s.incomeTarget, false)} goal · {money(s.netPerDeal, false)} net per deal
                </div>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-slate-500">
                    <tr>
                      <th className="py-1 font-medium">Step</th>
                      <th className="font-medium">Rate (from the step before)</th>
                      <th className="text-right font-medium">Per year</th>
                      <th className="text-right font-medium">Per month</th>
                      <th className="text-right font-medium">Per week</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      { label: 'Calls', rate: null as number | null, annual: s.calls, onRate: null as ((v: number) => void) | null },
                      { label: 'Meetings', rate: input.callToMeeting, annual: s.meetings, onRate: (v: number) => setStream(s.role, { callToMeeting: v }) },
                      ...s.steps.map((st, i) => ({
                        label: st.label,
                        rate: st.rate,
                        annual: st.annual,
                        onRate: (v: number) => setStream(s.role, { rates: input.rates.map((r, j) => (j === i ? v : r)) }),
                      })),
                    ].map((row) => (
                      <tr key={row.label}>
                        <td className="py-1.5">{row.label}</td>
                        <td>
                          {row.onRate ? (
                            <span className="inline-flex items-center gap-1">
                              <Input inputMode="decimal" value={pct(row.rate!)} onChange={(e) => row.onRate!((num(e.target.value) ?? 0) / 100)} className="!w-20 !py-0.5" aria-label={`${row.label} rate`} />%
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="text-right tabular-nums">{fmt(row.annual)}</td>
                        <td className="text-right tabular-nums">{fmt(row.annual / 12)}</td>
                        <td className="text-right tabular-nums">{fmt(row.annual / Math.max(1, a.workingWeeks))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
          <div className="rounded-md bg-indigo-50 p-3 text-sm text-indigo-900">
            In total: <b>{fmt(plan.perWeek.calls)} calls</b> and <b>{fmt(plan.perWeek.meetings)} meetings</b> a week (about {fmt(plan.perWeek.calls / 5)} calls a working day).
          </div>
          {!plan.realism.ok && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                This needs about <b>{fmt(plan.realism.requiredCallsPerWeek)} calls a week</b>, but your prospecting time allows about <b>{fmt(plan.realism.capacityPerWeek ?? 0)}</b>
                {u.secondJob ? ' (halved for your second job)' : ''}. Levers: target larger assets (more income per deal), improve your conversion rates, add prospecting hours, or lower the goal.
                You can still save it.
              </div>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldGroup label={`Urgency (from your private answers: ${tier})`}>
              <Choice
                options={[
                  { key: 'auto', label: `Use ${tier}` },
                  { key: 'low', label: 'Low' },
                  { key: 'medium', label: 'Medium' },
                  { key: 'high', label: 'High' },
                ]}
                value={override ?? 'auto'}
                onChange={(v) => setOverride(v === 'auto' ? null : (v as UrgencyTier))}
              />
              <p className="mt-1 text-xs text-slate-500">Changes the coach's tone and pace of check-ins, not your targets.</p>
            </FieldGroup>
            <Field label="Months to meaningful income" hint={`Suggested ${suggestedHorizon} from your answers. Revenue isn't marked behind before then.`}>
              <Input inputMode="numeric" value={horizon} onChange={(e) => setHorizonEdited(num(e.target.value) ?? 0)} className="!w-24" />
            </Field>
          </div>
        </div>
      )}

      <ErrorBox error={save.error} />
      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <Button variant="ghost" onClick={() => (step === 0 ? onDone() : setStep(step - 1))}>
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>
        {step < 3 ? (
          <Button variant="primary" disabled={!canNext} onClick={() => setStep(step + 1)}>
            Next
          </Button>
        ) : (
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save plan
          </Button>
        )}
      </div>
    </div>
  );
}

export const fmt = (n: number) => (n >= 100 ? Math.round(n).toLocaleString() : n >= 10 || Number.isInteger(n) ? n.toFixed(0) : n.toFixed(1));
export const Hint = ({ children }: { children: ReactNode }) => <p className="text-xs text-slate-500">{children}</p>;
export { METRIC_LABEL };
